import React, { useEffect, useState, useRef } from "react";
import "./AdminNavbar.css";

const NOTIFICATION_API =
    "/api/notifications";

function AdminNavbar() {

    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [showNotifications, setShowNotifications] = useState(false);

    const previousCount = useRef(0);

    // ==========================================
    // GET LOGGED-IN USER
    // ==========================================

    const getUser = () => {

        try {

            return JSON.parse(
                localStorage.getItem("user") || "{}"
            );

        } catch (error) {

            console.error(
                "User Parse Error:",
                error
            );

            return {};

        }

    };

    const user = getUser();

    // USER ID
    const userId = user?.id;


    // ==========================================
    // DESKTOP NOTIFICATION PERMISSION
    // ==========================================

    useEffect(() => {

        if ("Notification" in window) {

            if (
                Notification.permission === "default"
            ) {

                Notification.requestPermission();

            }

        }

    }, []);


    // ==========================================
    // FETCH USER NOTIFICATIONS
    // ==========================================

    const fetchNotifications = async () => {

        try {

            const token =
                localStorage.getItem("token");


            if (!token) {

                console.log(
                    "No login token found"
                );

                return;

            }


            if (!userId) {

                console.log(
                    "User ID not found in localStorage"
                );

                return;

            }


            // ==========================================
            // USER-SPECIFIC NOTIFICATION API
            // ==========================================

            const url =
                `${NOTIFICATION_API}/user/${userId}`;


            console.log(
                "Fetching Notifications:",
                url
            );


            const response = await fetch(

                url,

                {
                    method: "GET",

                    headers: {

                        "Authorization":
                            `Bearer ${token}`,

                        "Content-Type":
                            "application/json"

                    }

                }

            );


            if (!response.ok) {

                throw new Error(
                    `Notification API Error: ${response.status}`
                );

            }


            const data =
                await response.json();


            console.log(
                "USER NOTIFICATION RESPONSE:",
                data
            );


            // ==========================================
            // HANDLE API RESPONSE
            // ==========================================

            const notificationList =

                Array.isArray(data)

                    ? data

                    : data.notifications || [];


            setNotifications(
                notificationList
            );


            // ==========================================
            // FIND UNREAD
            // ==========================================

            const unreadList =
                notificationList.filter(
                    notification =>

                        notification.read === false ||

                        notification.isRead === false ||

                        notification.status === "UNREAD"
                );


            const unread =
                unreadList.length;


            // ==========================================
            // DESKTOP PUSH NOTIFICATION
            // ==========================================

            if (

                previousCount.current > 0 &&

                unread >
                previousCount.current &&

                "Notification" in window &&

                Notification.permission === "granted"

            ) {

                const latestNotification =
                    unreadList[0];


                if (latestNotification) {

                    new Notification(

                        latestNotification.title ||
                        "New CertED Notification",

                        {

                            body:
                                latestNotification.message ||
                                "You have a new notification.",

                            icon: "/logo192.png"

                        }

                    );

                }

            }


            previousCount.current =
                unread;


            setUnreadCount(
                unread
            );


        } catch (error) {

            console.error(
                "Notification API Error:",
                error
            );

        }

    };


    // ==========================================
    // INITIAL LOAD + AUTO REFRESH
    // ==========================================

    useEffect(() => {

        if (!userId) {
            return;
        }


        fetchNotifications();


        const interval =
            setInterval(

                fetchNotifications,

                10000

            );


        return () => {

            clearInterval(
                interval
            );

        };

    }, [userId]);


    // ==========================================
    // MARK SINGLE NOTIFICATION AS READ
    // ==========================================

    const markAsRead = async (
        notificationId
    ) => {

        try {

            const token =
                localStorage.getItem("token");


            const response =
                await fetch(

                    `${NOTIFICATION_API}/${notificationId}/read`,

                    {

                        method: "PUT",

                        headers: {

                            "Authorization":
                                `Bearer ${token}`,

                            "Content-Type":
                                "application/json"

                        }

                    }

                );


            if (!response.ok) {

                throw new Error(
                    "Failed to mark notification as read"
                );

            }


            setNotifications(
                prev =>

                    prev.map(
                        notification =>

                            notification.id ===
                            notificationId

                                ? {

                                    ...notification,

                                    read: true,

                                    isRead: true,

                                    status: "READ"

                                }

                                : notification
                    )

            );


            setUnreadCount(
                prev =>
                    Math.max(
                        prev - 1,
                        0
                    )
            );


        } catch (error) {

            console.error(
                "Mark Read Error:",
                error
            );

        }

    };


    // ==========================================
    // MARK ALL AS READ
    // ==========================================

    const markAllAsRead = async () => {

        try {

            const token =
                localStorage.getItem("token");


            const response =
                await fetch(

                    `${NOTIFICATION_API}/read-all`,

                    {

                        method: "PUT",

                        headers: {

                            "Authorization":
                                `Bearer ${token}`,

                            "Content-Type":
                                "application/json"

                        }

                    }

                );


            if (!response.ok) {

                throw new Error(
                    "Failed to mark all notifications as read"
                );

            }


            setNotifications(
                prev =>

                    prev.map(
                        notification => ({

                            ...notification,

                            read: true,

                            isRead: true,

                            status: "READ"

                        })
                    )

            );


            setUnreadCount(0);


        } catch (error) {

            console.error(
                "Mark All Read Error:",
                error
            );

        }

    };


    // ==========================================
    // FORMAT DATE
    // ==========================================

    const formatDate = (date) => {

        if (!date) {
            return "";
        }


        try {

            return new Date(
                date
            ).toLocaleString(

                "en-IN",

                {

                    day: "2-digit",

                    month: "short",

                    year: "numeric",

                    hour: "2-digit",

                    minute: "2-digit"

                }

            );

        } catch {

            return "";

        }

    };


    return (

        <nav className="admin-navbar">


            {/* ======================================
                LEFT
            ====================================== */}

            <div className="admin-navbar-left">
            </div>


            {/* ======================================
                RIGHT
            ====================================== */}

            <div className="admin-navbar-right">


                {/* ==================================
                    NOTIFICATION
                ================================== */}

                <div className="notification-container">

                    <button

                        className="notification-btn"

                        onClick={() =>
                            setShowNotifications(
                                prev => !prev
                            )
                        }

                        title="Notifications"

                    >

                        🔔


                        {unreadCount > 0 && (

                            <span className="notification-badge">

                                {unreadCount > 99
                                    ? "99+"
                                    : unreadCount}

                            </span>

                        )}

                    </button>


                    {/* ==================================
                        DROPDOWN
                    ================================== */}

                    {showNotifications && (

                        <div className="notification-dropdown">


                            {/* HEADER */}

                            <div className="notification-header">

                                <div>

                                    <strong>
                                        Notifications
                                    </strong>


                                    {unreadCount > 0 && (

                                        <span className="header-count">

                                            {unreadCount} unread

                                        </span>

                                    )}

                                </div>


                                {unreadCount > 0 && (

                                    <button

                                        className="mark-all-btn"

                                        onClick={
                                            markAllAsRead
                                        }

                                    >

                                        Mark all read

                                    </button>

                                )}

                            </div>


                            {/* LIST */}

                            <div className="notification-list">


                                {notifications.length === 0 ? (

                                    <div className="no-notification">

                                        <div className="empty-icon">
                                            🔔
                                        </div>

                                        <p>
                                            No notifications
                                        </p>

                                    </div>

                                ) : (

                                    notifications.map(
                                        notification => {

                                            const isUnread =

                                                notification.read === false ||

                                                notification.isRead === false ||

                                                notification.status === "UNREAD";


                                            return (

                                                <div

                                                    key={
                                                        notification.id
                                                    }

                                                    className={

                                                        `notification-item ${
                                                            isUnread
                                                                ? "unread"
                                                                : ""
                                                        }`

                                                    }

                                                    onClick={() => {

                                                        if (
                                                            isUnread
                                                        ) {

                                                            markAsRead(
                                                                notification.id
                                                            );

                                                        }

                                                    }}

                                                >


                                                    {/* ICON */}

                                                    <div className="notification-icon">

                                                        {
                                                            notification.type ===
                                                            "TRAINER"

                                                                ? "👨‍🏫"

                                                                : notification.type ===
                                                                  "PARTNER"

                                                                ? "🤝"

                                                                : notification.type ===
                                                                  "EMPLOYEE"

                                                                ? "👨‍💼"

                                                                : "🔔"
                                                        }

                                                    </div>


                                                    {/* CONTENT */}

                                                    <div className="notification-content">

                                                        <div className="notification-title">

                                                            {
                                                                notification.title ||
                                                                "New Notification"
                                                            }

                                                        </div>


                                                        <div className="notification-message">

                                                            {
                                                                notification.message ||
                                                                "You have a new notification."
                                                            }

                                                        </div>


                                                        <div className="notification-time">

                                                            {formatDate(

                                                                notification.createdAt ||

                                                                notification.createdDate ||

                                                                notification.date

                                                            )}

                                                        </div>

                                                    </div>


                                                    {/* UNREAD DOT */}

                                                    {isUnread && (

                                                        <span className="unread-dot"></span>

                                                    )}

                                                </div>

                                            );

                                        }

                                    )

                                )}

                            </div>

                        </div>

                    )}

                </div>


                {/* ==================================
                    ADMIN USER
                ================================== */}

                <div className="admin-user">

                    <div className="admin-avatar">

                        {user.fullName

                            ? user.fullName
                                .charAt(0)
                                .toUpperCase()

                            : "A"}

                    </div>


                    <div className="admin-user-info">

                        <span className="admin-name">

                            {user.fullName ||
                                "Admin"}

                        </span>


                        <span className="admin-role">

                            {user.role ||
                                "ADMIN"}

                        </span>

                    </div>

                </div>


            </div>

        </nav>

    );

}

export default AdminNavbar;